import React, { useLayoutEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import {
  DEMO_PREVIEW_STORAGE_KEY,
  demoPreviewBootStorageKey,
  activateDemoPreviewTabSession,
} from '../../utils/demoPreviewStorage';

/** Survives React StrictMode double layout effect in dev (same bootId must not consume payload twice). */
const demoBootLifecycleHandled = new Set();

/**
 * Consumes a one-time boot payload written by Demo Users → View as, applies demo JWTs in this window,
 * stores admin tokens in sessionStorage for Exit preview, then hard-navigates to the dashboard.
 * Must stay outside TopLevel so the first paint can swap tokens before auth redirects.
 */
export default function DemoPreviewBootstrap() {
  const { bootId } = useParams();
  const [error, setError] = useState(null);

  useLayoutEffect(() => {
    if (!bootId) {
      setError('Missing preview session id.');
      return;
    }
    if (demoBootLifecycleHandled.has(bootId)) {
      window.location.replace('/dashboard');
      return;
    }
    const key = demoPreviewBootStorageKey(bootId);
    const raw = localStorage.getItem(key);
    if (!raw) {
      setError(
        'This preview link was already used or is invalid. Close this tab, go back to Administration → Demo / Test Users — Actual View, and click View as again.'
      );
      return;
    }
    try {
      const p = JSON.parse(raw);
      if (!p.demoToken || p.adminToken == null) {
        setError('Invalid preview payload.');
        return;
      }
      demoBootLifecycleHandled.add(bootId);
      localStorage.removeItem(key);
      sessionStorage.setItem(
        DEMO_PREVIEW_STORAGE_KEY,
        JSON.stringify({
          token: p.adminToken,
          refreshToken: p.adminRefresh ?? null,
          viewingAsLabel: p.viewingAsLabel || 'Demo user',
        })
      );
      activateDemoPreviewTabSession(p.demoToken, p.demoRefresh || '');
      window.location.replace('/dashboard');
    } catch (e) {
      console.error('DemoPreviewBootstrap', e);
      setError('Could not start preview session.');
    }
  }, [bootId]);

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 520, mx: 'auto', mt: 4 }}>
        <Typography variant="h6" gutterBottom>
          Preview could not open
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          {error}
        </Typography>
        <Button component={Link} to="/login" variant="contained">
          Go to login
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: '#ffebee',
        p: 2,
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box
        sx={{
          maxWidth: 520,
          width: '100%',
          p: 3,
          border: '4px solid #c62828',
          borderRadius: 2,
          bgcolor: '#fff',
          boxShadow: 6,
        }}
      >
        <Alert severity="error" sx={{ mb: 2, fontWeight: 700 }}>
          Users preview mode — Administrator
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          You are entering a live preview session as a demo mailbox user. The next screen will show the real application
          with that user&apos;s permissions. This window is framed in <strong>red</strong> on purpose.
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 2 }}>
          <CircularProgress sx={{ color: '#c62828' }} />
          <Typography variant="body2" color="text.secondary">
            Opening demo preview…
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
